import { useCatalogAdminCapability } from '@frontend/features/hotel-catalog-admin/queries';
import { Badge, Box, Divider, Stack, styled, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  AppRegistration,
  Dashboard,
  Group,
  ChevronRight,
  Work,
  PermContactCalendar,
  Storefront,
  Phone,
  TravelExplore,
  Inbox,
} from '@mui/icons-material';
import { Can } from '@casl/react';

import { useSidebar } from '../../contexts/SidebarContext/hook';
import { DashboardButtonLink } from '../DashboardButtonLink';
import { Drawer, OpenIconButton } from './styles';

import { routes } from '@frontend/router/routes';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { useSiteConfig } from '@frontend/features/common/components/SiteConfigContext';
import { isInboxEnabled } from '@frontend/features/inbox/access';
import { useInbox } from '@frontend/features/inbox/InboxContext';
import {
  FLIGHT_SEARCH_UNDER_HEADER_HEIGHT,
  HOTEL_SEARCH_UNDER_HEADER_HEIGHT,
} from '@frontend/features/common/constants/layout';

interface StyledBoxProps {
  isOpen: boolean;
  paddingTop: number;
  isMobileCompressed: boolean;
}

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'hasNavSearch' && prop !== 'isMobileCompressed',
})<StyledBoxProps>(({ theme, isOpen, paddingTop, isMobileCompressed }) => ({
  paddingTop: paddingTop ? paddingTop : 0,
  marginTop: isMobileCompressed ? theme.spacing(5) : 0,
  display: 'flex',
  height: 40,
  transition: '.4s',
  alignItems: 'center',
  justifyContent: isOpen ? 'flex-end' : 'center',
}));

const determineStyledBoxHeight = (hasHotels: boolean, hasFlights: boolean) => {
  let result = 0;
  if (hasHotels) {
    result = HOTEL_SEARCH_UNDER_HEADER_HEIGHT;
  } else if (hasFlights) {
    result = FLIGHT_SEARCH_UNDER_HEADER_HEIGHT;
  }
  return result;
};

export const DashboardSidebar = () => {
  const theme = useTheme();
  const { ability } = useAuth();
  const { settings } = useSiteConfig();
  const { unreadCaseCount } = useInbox();
  const catalogAdmin = useCatalogAdminCapability();
  const { isOpen, handleSidebarOpening, hasNavHotelSearch, hasNavFlightsSearch } = useSidebar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const postaBrakepoint = useMediaQuery('(min-width:758px)');

  const handleButtonLinkClick = (link: string) => () => {
    navigate(link, { replace: true });
  };

  return (
    ability && (
      <Drawer
        sx={{
          zIndex: 2,
        }}
        variant="permanent"
        open={isOpen}
      >
        <>
          <StyledBox
            isOpen={isOpen}
            paddingTop={determineStyledBoxHeight(hasNavHotelSearch, hasNavFlightsSearch)}
            isMobileCompressed={!postaBrakepoint}
          >
            <OpenIconButton
              size="small"
              aria-label={t(`common.actions.${isOpen ? 'open' : 'close'}`)}
              onClick={handleSidebarOpening}
              isSidebarOpen={isOpen}
            >
              <ChevronRight />
            </OpenIconButton>
          </StyledBox>
          {isOpen ? (
            <Box display="flex" gap={1} alignItems="center" padding={(theme) => theme.spacing(1, 0)}>
              <Divider aria-hidden="true" sx={{ width: '24px' }} />
              <Typography variant="body2" color={theme.palette.disabled.main} textTransform="uppercase">
                {t('common.general')}
              </Typography>
            </Box>
          ) : (
            <Divider
              sx={{
                width: 48,
                margin: (theme) => theme.spacing(2.25, 0),
              }}
              aria-hidden="true"
              variant="middle"
            />
          )}

          <Stack gap={1}>
            <Can I="view" a="Dashboard" ability={ability}>
              <DashboardButtonLink
                icon={<Dashboard />}
                isActive={location.pathname === routes.dashboard.index}
                label={t('common.nav.dashboard')}
                onClick={handleButtonLinkClick(routes.dashboard.index)}
              />
            </Can>
            <Can I="view" a="TravelServices" ability={ability}>
              <DashboardButtonLink
                icon={<TravelExplore />}
                isActive={location.pathname.includes(routes.dashboard.travelBookings)}
                label={t('common.nav.travelBookings')}
                onClick={handleButtonLinkClick(routes.dashboard.travelBookings)}
              />
            </Can>
            <Can I="view" a="Calls" ability={ability}>
              <DashboardButtonLink
                icon={<Phone />}
                isActive={location.pathname.includes(routes.dashboard.calls)}
                label={t('common.nav.calls')}
                onClick={handleButtonLinkClick(routes.dashboard.calls)}
              />
            </Can>
            {isInboxEnabled(settings?.featureFlags) && (
              <Can I="manage" a="Cases" ability={ability}>
                <DashboardButtonLink
                  icon={
                    <Badge badgeContent={unreadCaseCount} color="error" max={99}>
                      <Inbox />
                    </Badge>
                  }
                  isActive={location.pathname.includes(routes.dashboard.inbox)}
                  label={t('common.nav.inbox')}
                  onClick={handleButtonLinkClick(
                    unreadCaseCount > 0 ? `${routes.dashboard.inbox}?unread=1` : routes.dashboard.inbox,
                  )}
                />
              </Can>
            )}
            <Can I="view" a="Cases" ability={ability}>
              <DashboardButtonLink
                icon={<Work />}
                isActive={location.pathname.includes(routes.dashboard.cases)}
                label={t('common.nav.cases')}
                onClick={handleButtonLinkClick(routes.dashboard.cases)}
              />
            </Can>
            <Can I="view" a="Clients" ability={ability}>
              <DashboardButtonLink
                icon={<PermContactCalendar />}
                isActive={location.pathname.includes(routes.dashboard.clients)}
                label={t('common.nav.clients')}
                onClick={handleButtonLinkClick(routes.dashboard.clients)}
              />
            </Can>
            <Can I="view" a="Agencies" ability={ability}>
              <DashboardButtonLink
                icon={<Storefront />}
                isActive={location.pathname.includes(routes.dashboard.agencies)}
                label={t('common.nav.agencies')}
                onClick={handleButtonLinkClick(routes.dashboard.agencies)}
              />
            </Can>
            <Can I="manage" a="Users" ability={ability}>
              <DashboardButtonLink
                icon={<Group />}
                isActive={location.pathname.includes(routes.dashboard.users)}
                label={t('common.nav.users')}
                onClick={handleButtonLinkClick(routes.dashboard.users)}
              />
            </Can>
            {!catalogAdmin.data?.allowed && <Can I="view" a="ContentManager" ability={ability}>
              <DashboardButtonLink
                icon={<AppRegistration />}
                isActive={location.pathname.includes(routes.dashboard.content.hotel.list)}
                label={t('hotels.contentManager.title')}
                onClick={handleButtonLinkClick(routes.dashboard.content.hotel.list)}
              />
            </Can>}
          </Stack>
        </>
      </Drawer>
    )
  );
};
